import { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Banknote, Check, X, Loader2, CheckCircle2 } from "lucide-react";
import api from "../api";

const money = (v) => `KES ${Number(v || 0).toLocaleString()}`;
const STATUS_STYLE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-blue-100 text-blue-800",
  paid: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-700",
};

export default function AdminWithdrawals() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/payments/wallet/admin/withdrawals/${filter ? `?status=${filter}` : ""}`);
      setRows(res.data || []);
    } catch (e) {
      setMsg("Couldn't load withdrawals (are you an admin?).");
    } finally { setLoading(false); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(""), 4000); };

  const process = async (id, action) => {
    let body = { action };
    if (action === "reject") {
      const reason = window.prompt("Reason for rejecting (optional):", "");
      if (reason === null) return;
      body.reason = reason;
    }
    if (action === "paid") {
      const ref = window.prompt("M-Pesa payout reference (optional):", "");
      if (ref === null) return;
      body.mpesa_reference = ref;
    }
    try {
      await api.post(`/payments/wallet/admin/withdrawals/${id}/process/`, body);
      flash(`Marked ${action}.`);
      load();
    } catch (e) { flash("Action failed."); }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <Helmet><title>Withdrawals | EventSawa Admin</title></Helmet>
      <div className="max-w-5xl mx-auto px-4 space-y-5">
        <div className="flex items-center gap-2">
          <Banknote className="h-6 w-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Withdrawal requests</h1>
        </div>

        <div className="flex gap-2">
          {["pending", "approved", "paid", "rejected", ""].map((f) => (
            <button key={f || "all"} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-sm ${filter === f ? "bg-blue-600 text-white" : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"}`}>
              {f ? f[0].toUpperCase() + f.slice(1) : "All"}
            </button>
          ))}
        </div>

        {msg && <div className="text-sm text-blue-600 dark:text-blue-400">{msg}</div>}

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-gray-500 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg">
            No {filter || ""} withdrawal requests.
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((w) => (
              <div key={w.id} className="p-4 flex items-center gap-4 flex-wrap">
                <div className="flex-1 min-w-[12rem]">
                  <p className="font-semibold text-gray-900 dark:text-white">{money(w.amount)} <span className="text-gray-400 font-normal">→ {w.phone}</span></p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {w.organizer_name} · {(w.created_at || "").slice(0, 10)}{w.reason ? ` · ${w.reason}` : ""}{w.mpesa_reference ? ` · ref ${w.mpesa_reference}` : ""}
                  </p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${STATUS_STYLE[w.status] || ""}`}>{w.status}</span>
                {(w.status === "pending" || w.status === "approved") && (
                  <div className="flex gap-2">
                    {w.status === "pending" && (
                      <button onClick={() => process(w.id, "approve")}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 text-white text-sm">
                        <Check size={15} /> Approve
                      </button>
                    )}
                    <button onClick={() => process(w.id, "paid")}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-green-600 text-white text-sm">
                      <CheckCircle2 size={15} /> Mark paid
                    </button>
                    <button onClick={() => process(w.id, "reject")}
                      className="flex items-center gap-1 px-3 py-1.5 rounded-md border border-red-300 text-red-600 text-sm">
                      <X size={15} /> Reject
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
